import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { getSession } from '../lib/session'
import TimeSeriesExplorer from '../components/TimeSeriesExplorer'

// --- icon path library (ported from the design) ---
const IC = {
  brain: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M15 2v2M15 20v2M2 15h2M2 9h2M20 15h2M20 9h2M9 2v2M9 20v2"/>',
  dollar: '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>',
  bag: '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>',
  msg: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  inbox: '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>',
  flask: '<path d="M9 3h6M10 3v6l-4 8a2 2 0 0 0 2 3h8a2 2 0 0 0 2-3l-4-8V3"/>',
  plug: '<path d="M12 22v-5M9 8V2M15 8V2M18 8H6v4a6 6 0 0 0 12 0z"/>',
  target: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/>',
  zap: '<path d="M13 2 3 14h9l-1 8 10-12h-9z"/>',
}

function Icon({ name, color, className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={{ stroke: color || 'currentColor' }}
      dangerouslySetInnerHTML={{ __html: IC[name] || '' }} />
  )
}

// --- honest EMPTY state (real zeros until the workspace has data) ---
// Shown when the backend reports sample:true (no metrics/conversations/connectors
// yet) or on a fetch error. Never fabricates numbers — a new workspace sees zeros
// and a prompt to connect its data, not an invented demo.
const EMPTY = {
  sample: true,
  headline: {
    label: "The brain's headline today",
    parts: [
      { t: 'No live data yet — ' }, { t: 'connect your sources', b: true },
      { t: ' (analytics, ads, store, chat) and the command center fills in with your real numbers.' },
    ],
    cta: 'Connect your data →',
    ctaPath: '/connections',
  },
  kpis: [
    { l: 'Attributed revenue', v: '$0', d: '—', up: 1, c: '#4E9AC4', ic: 'dollar', s: [] },
    { l: 'Qualified leads', v: '0', d: '—', up: 1, c: '#7C6AE0', ic: 'users', s: [] },
    { l: 'Orders', v: '0', d: '—', up: 1, c: '#2E9E5B', ic: 'bag', s: [] },
    { l: 'Blended ROAS', v: '—', d: '—', up: 1, c: '#4E9AC4', ic: 'target', s: [] },
    { l: 'Messages sent', v: '0', d: '—', up: 1, c: '#0D9488', ic: 'msg', s: [] },
    { l: 'Active conversations', v: '0', d: '—', up: 1, c: '#C77D19', ic: 'inbox', s: [] },
    { l: 'Experiments running', v: '0', d: '—', up: 1, c: '#8E7CC3', ic: 'flask', s: [] },
    { l: 'Connected sources', v: '0', d: 'connect', up: 1, c: '#4E9AC4', ic: 'plug', s: [] },
  ],
  series: { revenue: [], spend: [], leads: [] },
  moves: [
    { t: 'Connect your first data source (GA4, Google Ads, Stripe, or your store) so the brain can reason over your real numbers.', tag: 'Get started', tc: '#4E9AC4', imp: 'unlocks analytics' },
  ],
  services: [],
}

const DOTC = { 1: '#2E9E5B', 2: '#C77D19', 3: '#E24A3B' }

function sparkPath(s, w, h) {
  if (!Array.isArray(s) || s.length < 2) return ''
  const mn = Math.min(...s), mx = Math.max(...s)
  return s.map((v, i) => `${(i / (s.length - 1) * w).toFixed(1)},${(h - (v - mn) / ((mx - mn) || 1) * h).toFixed(1)}`).join(' ')
}

// --- adapter: backend /overview shape -> this component's render shape ---
// Backend emits kpis {key,label,value,delta,up,spark}, services {key,name,status,detail}
// and headline as a plain string. The UI renders l/v/d/ic/c/s, n/ic/c/ok/v/path and a
// headline object. Map by well-known key so live tiles show icons, colours and values.
const KPI_META = {
  attributed_revenue:  { c: '#4E9AC4', ic: 'dollar' },
  qualified_leads:     { c: '#7C6AE0', ic: 'users' },
  orders:              { c: '#2E9E5B', ic: 'bag' },
  roas:                { c: '#E24A3B', ic: 'target' },
  messages_sent:       { c: '#0D9488', ic: 'msg' },
  active_conversations:{ c: '#C77D19', ic: 'inbox' },
  experiments_running: { c: '#8E7CC3', ic: 'flask' },
  connected_sources:   { c: '#4E9AC4', ic: 'plug' },
}
const SVC_META = {
  'marketing-intel': { ic: 'brain', c: '#7C6AE0', path: '/marketing' },
  growth:            { ic: 'flask', c: '#8E7CC3', path: '/growth-lab' },
  campaigns:         { ic: 'msg',   c: '#0D9488', path: '/campaigns' },
  commerce:          { ic: 'bag',   c: '#2E9E5B', path: '/commerce' },
  inbox:             { ic: 'inbox', c: '#C77D19', path: '/inbox' },
  connections:       { ic: 'plug',  c: '#4E9AC4', path: '/connections' },
}
const MOVE_TAGC = ['#4E9AC4', '#7C6AE0', '#C77D19', '#2E9E5B', '#0D9488']
const SVC_OK = { ok: 1, warn: 2, error: 3, critical: 3 }

function adaptOverview(d) {
  const kpis = Array.isArray(d.kpis) && d.kpis.length
    ? d.kpis.map((k, i) => {
        const meta = KPI_META[k.key] || { c: (EMPTY.kpis[i] || {}).c || '#4E9AC4', ic: (EMPTY.kpis[i] || {}).ic || 'zap' }
        return {
          l: k.label ?? '', v: k.value ?? '—', d: k.delta ?? '—',
          up: k.up ? 1 : 0, c: meta.c, ic: meta.ic,
          s: Array.isArray(k.spark) ? k.spark : [],
        }
      })
    : EMPTY.kpis
  const moves = Array.isArray(d.moves) && d.moves.length
    ? d.moves.map((m, i) => ({ t: m.t ?? '', tag: m.tech || 'Move', tc: MOVE_TAGC[i % MOVE_TAGC.length], imp: m.imp ?? '' }))
    : EMPTY.moves
  const services = Array.isArray(d.services) && d.services.length
    ? d.services.map((sv) => {
        const meta = SVC_META[sv.key] || { ic: 'zap', c: '#4E9AC4', path: '' }
        return { n: sv.name ?? '', ic: meta.ic, c: meta.c, ok: SVC_OK[sv.status] || 1, v: sv.detail ?? '', path: meta.path }
      })
    : EMPTY.services
  const headline = typeof d.headline === 'string' && d.headline.trim()
    ? { label: "The brain's headline today", parts: [{ t: d.headline }], cta: 'Open Growth Lab →', ctaPath: '/growth-lab' }
    : EMPTY.headline
  const series = d.series && Array.isArray(d.series.revenue) ? d.series : EMPTY.series
  return { headline, kpis, series, moves, services }
}

export default function Dashboard() {
  const session = getSession()
  const navigate = useNavigate()
  const [data, setData] = useState(EMPTY)
  const [sample, setSample] = useState(true)
  // Onboarding due-diligence — the Onboarding Agent's live setup checklist.
  const [onboarding, setOnboarding] = useState(null)

  useEffect(() => {
    if (!session?.namespaceId) return
    let live = true
    axios.get('/api/v1/marketing-intel/overview')
      .then(res => {
        if (!live) return
        const d = res.data || {}
        // Empty/unknown workspace -> show the representative demo (honestly ribboned).
        if (d.sample) { setData(EMPTY); setSample(true); return }
        // Live data -> map the backend contract onto the render shape.
        setData(adaptOverview(d))
        setSample(false)
      })
      .catch(() => { if (live) { setData(EMPTY); setSample(true) } })
    // Setup checklist — hidden once the workspace is fully configured (100%).
    axios.get('/api/v1/agent/onboarding')
      .then(res => { if (live) setOnboarding(res.data || null) })
      .catch(() => { if (live) setOnboarding(null) })
    return () => { live = false }
  }, [session?.namespaceId])

  if (!session?.namespaceId) {
    return (
      <div className="cc-root">
        <CCStyle />
        <div className="cc-wrap">
          <h1 className="cc-h1">Command center</h1>
          <p className="cc-sub">Not connected — <a className="cc-link" href="/setup">visit Setup</a> to connect a tenant, then the brain lights up here.</p>
        </div>
      </div>
    )
  }

  const hl = data.headline || EMPTY.headline
  const go = (p) => { if (p) navigate(p) }

  return (
    <div className="cc-root">
      <CCStyle />
      <div className="cc-wrap">
        <div>
          <h1 className="cc-h1">Command center</h1>
          <p className="cc-sub">Every service, one view — what happened, why, and the next move.</p>
        </div>

        {sample && (
          <div className="cc-ribbon">No live data yet — connect your sources to see live numbers</div>
        )}

        {/* onboarding checklist — the Onboarding Agent's setup progress. Hidden at 100%. */}
        {onboarding && Array.isArray(onboarding.steps) && onboarding.steps.length > 0 && (onboarding.progressPct ?? 0) < 100 && (
          <div className="cc-onb">
            <div className="cc-onb-head">
              <div>
                <p className="cc-lb">Get set up</p>
                <h3 className="cc-onb-h">{onboarding.greeting || 'Finish setting up your workspace'}</h3>
              </div>
              <span className="cc-onb-pct">{onboarding.progressPct ?? 0}%</span>
            </div>
            <div className="cc-onb-bar"><span style={{ width: `${onboarding.progressPct ?? 0}%` }} /></div>
            <div className="cc-onb-steps">
              {onboarding.steps.map((s) => {
                const isNext = onboarding.nextStep && onboarding.nextStep.key === s.key
                return (
                  <div key={s.key} className={`cc-onb-step ${s.done ? 'done' : ''} ${isNext ? 'next' : ''}`}>
                    <span className="cc-onb-mark">{s.done ? '✓' : '○'}</span>
                    <span className="cc-onb-lbl">{s.label}</span>
                    {!s.done && s.href && (
                      <button className="cc-onb-cta" onClick={() => go(s.href)}>{s.cta || 'Set up'} →</button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* hero */}
        <div className="cc-hero">
          <span className="cc-heroic"><Icon name="brain" color="#fff" /></span>
          <div style={{ flex: 1 }}>
            <p className="cc-lb">{hl.label || "The brain's headline today"}</p>
            <h2 className="cc-heroh">
              {Array.isArray(hl.parts)
                ? hl.parts.map((p, i) => p.b ? <b key={i}>{p.t}</b> : <React.Fragment key={i}>{p.t}</React.Fragment>)
                : (typeof hl.text === 'string' ? hl.text : '')}
            </h2>
            <button className="cc-act" onClick={() => go(hl.ctaPath || '/growth-lab')}>{hl.cta || 'Open Growth Lab →'}</button>
          </div>
        </div>

        {/* kpi grid */}
        <div className="cc-kgrid">
          {(data.kpis || []).map((k, i) => (
            <div key={i} className="cc-kpi" style={{ '--cc-nc': k.c }}>
              <p className="cc-kl"><Icon name={k.ic} color={k.c} /> {k.l}</p>
              <div className="cc-kv">{k.v}</div>
              <div className={`cc-kd ${k.up ? 'cc-up' : 'cc-down'}`}>{k.up ? '▲' : '▼'} {k.d}</div>
              <svg className="cc-spark" width="60" height="20">
                <polyline points={sparkPath(k.s, 58, 18)} fill="none" stroke={k.c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          ))}
        </div>

        {/* two-col: chart + moves */}
        <div className="cc-cols">
          <div className="cc-card">
            <div className="cc-cardh"><h3>Revenue</h3><span>candles · area · bars</span></div>
            {/* Financial-style explorer — replaces the flat spend→revenue canvas.
                Its own chart-type / interval / range controls and dimension tabs. */}
            <TimeSeriesExplorer metric="revenue" title="Revenue over time" color="#4E9AC4" />
          </div>
          <div className="cc-card">
            <h3 style={{ margin: '0 0 2px', fontSize: 15 }}>The brain's top moves</h3>
            <p style={{ fontSize: 12, color: 'var(--cc-dim)', fontWeight: 600, margin: 0 }}>cross-service · evidence-backed</p>
            <div>
              {(data.moves || []).map((m, i) => (
                <div key={i} className="cc-ins">
                  <p className="cc-it">{m.t}</p>
                  <div className="cc-ib">
                    <span className="cc-tag" style={{ background: m.tc }}>{m.tag}</span>
                    <span className="cc-mini cc-up">{m.imp}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* service strip */}
        <p className="cc-sec-lb">Your services — health at a glance</p>
        <div className="cc-svc-grid">
          {(data.services || []).map((s, i) => (
            <div key={i} className="cc-svc" style={{ '--cc-nc': s.c }} onClick={() => go(s.path)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') go(s.path) }}>
              <div className="cc-sh">
                <Icon name={s.ic} color={s.c} />
                <span className="cc-sn">{s.n}</span>
                <span className="cc-dot" style={{ background: DOTC[s.ok] || '#A4ABB2' }} />
              </div>
              <p className="cc-sv" dangerouslySetInnerHTML={{ __html: s.v || '' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CCStyle() {
  return (
    <style>{`
    .cc-root{
      --cc-bg:#FBFBF7;--cc-panel:#FFFFFF;--cc-panel2:#F5F5EF;--cc-line:#EAE9E1;--cc-ink:#2A343E;--cc-mut:#6C7681;--cc-dim:#A4ABB2;
      --cc-accent:#4E9AC4;--cc-brain:#7C6AE0;--cc-up:#2E9E5B;--cc-down:#E24A3B;--cc-warn:#C77D19;
      --cc-shadow:0 1px 2px rgba(42,52,62,.05),0 8px 22px rgba(42,52,62,.06);
      background:var(--cc-bg);color:var(--cc-ink);
      font-family:Nunito,system-ui,-apple-system,sans-serif;-webkit-font-smoothing:antialiased;
      margin:-24px;padding:26px 22px;min-height:calc(100% + 48px);
    }
    [data-theme="dark"] .cc-root{
      --cc-bg:#0D1015;--cc-panel:#161A21;--cc-panel2:#12151B;--cc-line:#252B34;--cc-ink:#E7ECF1;--cc-mut:#98A2AD;--cc-dim:#5C6672;
      --cc-accent:#5FB0DB;--cc-brain:#9C8CF0;--cc-up:#5CC47C;--cc-down:#F0575C;--cc-warn:#E0A73E;
      --cc-shadow:0 1px 2px rgba(0,0,0,.3),0 12px 30px rgba(0,0,0,.5);
    }
    @media(prefers-color-scheme:dark){:root:not([data-theme="light"]) .cc-root{
      --cc-bg:#0D1015;--cc-panel:#161A21;--cc-panel2:#12151B;--cc-line:#252B34;--cc-ink:#E7ECF1;--cc-mut:#98A2AD;--cc-dim:#5C6672;
      --cc-accent:#5FB0DB;--cc-brain:#9C8CF0;--cc-up:#5CC47C;--cc-down:#F0575C;--cc-warn:#E0A73E;--cc-shadow:0 1px 2px rgba(0,0,0,.3),0 12px 30px rgba(0,0,0,.5);
    }}
    .cc-root *{box-sizing:border-box}
    .cc-wrap{max-width:1120px;margin:0 auto;}
    .cc-h1{font-size:25px;font-weight:900;letter-spacing:-.02em;margin:0;color:var(--cc-ink);}
    .cc-sub{color:var(--cc-mut);font-size:14px;font-weight:500;margin:3px 0 0;}
    .cc-link{color:var(--cc-accent);font-weight:700;text-decoration:underline;}
    .cc-ribbon{margin-top:14px;display:inline-block;background:var(--cc-panel2);border:1px solid var(--cc-line);color:var(--cc-mut);font-size:12px;font-weight:800;border-radius:20px;padding:6px 13px;}
    /* onboarding checklist */
    .cc-onb{margin-top:16px;background:var(--cc-panel);border:1px solid var(--cc-line);border-radius:16px;padding:18px 20px;box-shadow:var(--cc-shadow);}
    .cc-onb-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
    .cc-onb-h{font-size:16px;font-weight:800;margin:2px 0 0;color:var(--cc-ink);letter-spacing:-.01em;line-height:1.35;}
    .cc-onb-pct{flex:none;font-size:15px;font-weight:900;color:var(--cc-brain);font-variant-numeric:tabular-nums;}
    .cc-onb-bar{margin:12px 0 4px;height:7px;border-radius:6px;background:var(--cc-panel2);border:1px solid var(--cc-line);overflow:hidden;}
    .cc-onb-bar span{display:block;height:100%;background:var(--cc-brain);border-radius:6px;transition:width .3s ease;}
    .cc-onb-steps{margin-top:10px;display:flex;flex-direction:column;gap:2px;}
    .cc-onb-step{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:10px;}
    .cc-onb-step.next{background:var(--cc-panel2);}
    .cc-onb-mark{flex:none;width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:900;background:var(--cc-panel2);color:var(--cc-dim);border:1px solid var(--cc-line);}
    .cc-onb-step.done .cc-onb-mark{background:var(--cc-up);color:#fff;border-color:var(--cc-up);}
    .cc-onb-lbl{flex:1;font-size:13.5px;font-weight:700;color:var(--cc-ink);}
    .cc-onb-step.done .cc-onb-lbl{color:var(--cc-mut);}
    .cc-onb-cta{flex:none;font-size:12px;font-weight:800;color:#fff;background:var(--cc-brain);border:none;border-radius:9px;padding:6px 12px;cursor:pointer;font-family:inherit;}
    .cc-onb-cta:hover{filter:brightness(.96);}
    /* hero */
    .cc-hero{margin-top:18px;background:linear-gradient(120deg,rgba(124,106,224,.10),rgba(78,154,196,.06) 60%,transparent);border:1px solid var(--cc-line);border-radius:18px;padding:20px 22px;display:flex;gap:16px;align-items:flex-start;box-shadow:var(--cc-shadow);}
    .cc-heroic{flex:none;width:44px;height:44px;border-radius:13px;background:var(--cc-brain);display:flex;align-items:center;justify-content:center;}
    .cc-heroic svg{width:23px;height:23px;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
    .cc-lb{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:var(--cc-brain);margin:0 0 4px;}
    .cc-heroh{font-size:19px;font-weight:800;margin:0;letter-spacing:-.01em;line-height:1.3;color:var(--cc-ink);}
    .cc-heroh b{color:var(--cc-brain);}
    .cc-act{margin-top:10px;display:inline-flex;gap:7px;align-items:center;font-size:13px;font-weight:800;color:#fff;background:var(--cc-brain);border:none;border-radius:10px;padding:8px 14px;cursor:pointer;font-family:inherit;}
    /* kpi grid */
    .cc-kgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:16px;}
    @media(max-width:820px){.cc-kgrid{grid-template-columns:repeat(2,1fr)}}
    .cc-kpi{background:var(--cc-panel);border:1px solid var(--cc-line);border-radius:14px;padding:14px 15px;box-shadow:var(--cc-shadow);position:relative;overflow:hidden;}
    .cc-kl{font-size:12px;color:var(--cc-mut);font-weight:700;margin:0;display:flex;align-items:center;gap:6px;}
    .cc-kl svg{width:14px;height:14px;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
    .cc-kv{font-size:24px;font-weight:900;letter-spacing:-.02em;margin:6px 0 2px;font-variant-numeric:tabular-nums;color:var(--cc-ink);}
    .cc-kd{font-size:12px;font-weight:800;}
    .cc-spark{position:absolute;right:10px;top:12px;opacity:.5;}
    .cc-up{color:var(--cc-up)}.cc-down{color:var(--cc-down)}
    /* two-col */
    .cc-cols{display:grid;grid-template-columns:1.5fr 1fr;gap:16px;margin-top:16px;}
    @media(max-width:820px){.cc-cols{grid-template-columns:1fr}}
    .cc-card{background:var(--cc-panel);border:1px solid var(--cc-line);border-radius:16px;padding:18px 20px;box-shadow:var(--cc-shadow);}
    .cc-cardh{display:flex;justify-content:space-between;align-items:baseline;margin:0 0 4px;}
    .cc-cardh h3{font-size:15px;font-weight:800;margin:0;color:var(--cc-ink);}
    .cc-cardh span{font-size:12px;color:var(--cc-dim);font-weight:600;}
    .cc-legend{display:flex;gap:14px;margin:10px 0 6px;}
    .cc-legend b{font-size:12px;font-weight:700;color:var(--cc-mut);display:flex;align-items:center;gap:6px;}
    .cc-legend i{width:10px;height:10px;border-radius:3px;display:inline-block;}
    /* insights */
    .cc-ins{border:1px solid var(--cc-line);border-radius:12px;padding:13px 14px;margin-top:10px;background:var(--cc-panel2);}
    .cc-it{font-size:13.5px;font-weight:700;margin:0;line-height:1.45;color:var(--cc-ink);}
    .cc-ib{display:flex;gap:7px;flex-wrap:wrap;align-items:center;margin-top:8px;}
    .cc-tag{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;padding:3px 8px;border-radius:7px;color:#fff;}
    .cc-mini{font-size:11.5px;font-weight:700;color:var(--cc-mut);}
    /* service strip */
    .cc-svc-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:16px;}
    @media(max-width:820px){.cc-svc-grid{grid-template-columns:repeat(2,1fr)}}
    .cc-svc{background:var(--cc-panel);border:1px solid var(--cc-line);border-radius:13px;padding:13px 15px;box-shadow:var(--cc-shadow);cursor:pointer;transition:.15s;}
    .cc-svc:hover{transform:translateY(-2px);border-color:var(--cc-nc);}
    .cc-sh{display:flex;align-items:center;gap:8px;}
    .cc-sh svg{width:16px;height:16px;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
    .cc-sn{font-size:13.5px;font-weight:800;color:var(--cc-ink);}
    .cc-dot{width:8px;height:8px;border-radius:50%;margin-left:auto;}
    .cc-sv{font-size:12.5px;color:var(--cc-mut);font-weight:600;margin:7px 0 0;}
    .cc-sv b{color:var(--cc-ink);font-weight:800;}
    .cc-sec-lb{font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:var(--cc-dim);margin:24px 0 2px;}
    `}</style>
  )
}
